import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { ListSpecDateResponse } from '@api/models/TariffEditorApi/Contract/SpecDate/list-spec-date-response';
import { Observable } from 'rxjs';
import { SpecialDatesService } from '@api/services/special-dates.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { AddSpecDateListRequest } from '@api/models/TariffEditorApi/Contract/SpecDate/add-spec-date-list-request';
import { AddSpecDateListResponse } from '@api/models/TariffEditorApi/Contract/SpecDate/add-spec-date-list-response';
import { SetSpecDateNameRequest } from '@api/models/TariffEditorApi/Contract/SpecDate/set-spec-date-name-request';
import {
    ListSpecDateConditionResponse,
} from '@api/models/TariffEditorApi/Contract/SpecDate/list-spec-date-condition-response';
import { GetSpecDateDataResponse } from '@api/models/TariffEditorApi/Contract/SpecDate/get-spec-date-data-response';
import {
    AddSpecDateConditionRequest,
} from '@api/models/TariffEditorApi/Contract/SpecDate/add-spec-date-condition-request';
import {
    AddSpecDateConditionResponse,
} from '@api/models/TariffEditorApi/Contract/SpecDate/add-spec-date-condition-response';
import { CustomRxState } from '@core/state/rx.state';


interface ISpecificDatesState {
    loading: Record<StartWithUnit, boolean>;
    specificDates: ListSpecDateResponse[];
    specificDateConditions: ListSpecDateConditionResponse[];
    specificDate: GetSpecDateDataResponse;
}

interface SpecificDatesAction {
    requestSpecificDates: ListSpecDateResponse[];
    requestSpecificDateConditions: ListSpecDateConditionResponse[];
    requestSpecificDate: GetSpecDateDataResponse;
}

@Injectable()
export class SpecificDatesState extends CustomRxState<ISpecificDatesState> {
    readonly #specialDatesService: SpecialDatesService = inject(SpecialDatesService);
    readonly #actions: RxActions<SpecificDatesAction> = new RxActionFactory<SpecificDatesAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly specificDates$: Observable<ListSpecDateResponse[]> = this.select('specificDates');
    public readonly specificDate$: Observable<GetSpecDateDataResponse> = this.select('specificDate');
    public readonly specificDateConditions$: Observable<ListSpecDateConditionResponse[]> = this.select('specificDateConditions');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestSpecificDates();
    }

    public requestSpecificDates(): void {
        this.hold(
            this.#specialDatesService.apiTariffListSpecDateGet(),
            this.#actions.requestSpecificDates,
        );
    }

    public requestSpecificDate(listId: number): void {
        this.hold(
            this.#specialDatesService.apiTariffGetSpecDateDataGet({ listId }),
            this.#actions.requestSpecificDate,
        );
    }

    public requestSpecificDateConditions(listId: number): void {
        this.hold(
            this.#specialDatesService.apiTariffListSpecDateConditionGet({ listId }),
            this.#actions.requestSpecificDateConditions,
        );
    }

    public setSpecificDate(date: GetSpecDateDataResponse): void {
        this.#actions.requestSpecificDate(date);
    }

    public createSpecificDate(body: AddSpecDateListRequest): Observable<AddSpecDateListResponse> {
        return this.#specialDatesService.apiTariffAddSpecDateListPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificDate),
            tap(() => this.requestSpecificDates()),
        );
    }

    public editSpecificDate(body: SetSpecDateNameRequest): Observable<void> {
        return this.#specialDatesService.apiTariffSetSpecDateNamePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificDate),
            tap(() => this.requestSpecificDates()),
        );
    }

    public deleteSpecificDate(listId: number): Observable<void> {
        return this.#specialDatesService.apiTariffRemoveSpecDateListDelete({ listId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteSpecificDate),
            tap(() => this.requestSpecificDates()),
        );
    }

    public deleteSpecificDateCondition(spdcId: number): Observable<void> {
        return this.#specialDatesService.apiTariffRemoveSpecDateConditionDelete({ spdcId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteSpecificDateConditional),
            tap(() => this.requestSpecificDateConditions(this.get('specificDate')?.listId)),
        );
    }

    public createSpecificDateCondition(body: AddSpecDateConditionRequest): Observable<AddSpecDateConditionResponse> {
        return this.#specialDatesService.apiTariffAddSpecDateConditionPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificDateConditional),
            tap(() => this.requestSpecificDateConditions(body.listId)),
        );
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            specificDates: null,
            specificDateConditions: null,
            specificDate: null,
        });
    }

    private connectSelectors(): void {
        this.connect('specificDates', this.#actions.requestSpecificDates$);
        this.connect('specificDateConditions', this.#actions.requestSpecificDateConditions$);
        this.connect('specificDate', this.#actions.requestSpecificDate$);
        this.connect('specificDate', this.#actions.requestSpecificDate$.pipe(
            filter(Boolean),
            distinctUntilChanged((a: GetSpecDateDataResponse, b: GetSpecDateDataResponse) => a.listId === b.listId),
            tap((date: GetSpecDateDataResponse) => {
                this.set({ 'specificDateConditions': null });
                this.requestSpecificDateConditions(date.listId);
            }),
        ));
    }
}
